import { Component } from "react"
import { useLocation } from "react-router-dom"
import ErrorState from "./ui/ErrorState"
import "./ui/ui.css"
import { HOME_PATH } from "../navigation/paths"

/**
 * ErrorBoundary (Stage 1.7) - catches render errors below it and shows a friendly fallback
 * instead of a blank screen. It does not change routing: "Try again" re-renders the same view,
 * and the dashboard link is an ordinary link to the existing /company/dashboard route.
 *
 * Props:
 *  - resetKey: when it changes (e.g. the route path), a previous error is cleared automatically
 *  - title / message: fallback copy
 *  - fullPage: centre the fallback in the viewport (used for the app-level boundary)
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
    this.reset = this.reset.bind(this)
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Keep the details in the console for support/debugging; the user sees the friendly fallback.
    console.error("[ErrorBoundary] Unhandled render error:", error, info?.componentStack)
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset()
    }
  }

  reset() {
    this.setState({ error: null })
  }

  render() {
    if (!this.state.error) return this.props.children

    const {
      title = "Something went wrong on this page",
      message = "An unexpected error stopped this page from loading. You can try again or go back to the dashboard.",
      fullPage = false,
    } = this.props

    return (
      <div
        className="vy-error-boundary"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: fullPage ? "100vh" : "60vh",
          padding: "var(--vy-space-6, 1.5rem)",
        }}
      >
        <div style={{ maxWidth: 520, width: "100%", textAlign: "center" }}>
          <ErrorState title={title} message={message} onRetry={this.reset} retryLabel="Try again" />
          <a href={HOME_PATH} className="vy-btn vy-btn--secondary" style={{ marginTop: "var(--vy-space-3, 0.75rem)" }}>
            Go to dashboard
          </a>
        </div>
      </div>
    )
  }
}

/** Route-level boundary: resets automatically when the user navigates to another path. */
export function RouteErrorBoundary({ children }) {
  const location = useLocation()
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
}

export default ErrorBoundary
